/**
 * PendingPolicies — V96-4-9 round 3. Tells the current member that published
 * policies are waiting for their acknowledgment, and deep-links to the
 * Policies page (V98-3-1), which opens the acknowledge drawer on arrival
 * (`?acknowledge=1`).
 * Reads (any member): GET /policies, GET /policies/mine.
 */
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { StatusGlyph } from '@aigovops/design-system';
import { listMyPolicyAcknowledgments, listPublishedSoc2Policies, type PublishedPolicy } from '../lib/api';
import { useOptionalWorkspace } from '../lib/workspace';
import './pending-policies.css';

export const pendingPolicyKeys = {
  published: (slug: string) => ['soc2-policies', slug] as const,
  mine: (slug: string) => ['soc2-policies-mine', slug] as const,
};

/** Published policies that require acknowledgment and that this member has not acknowledged (this version). */
export function usePendingPolicies(slug: string) {
  const published = useQuery({ queryKey: pendingPolicyKeys.published(slug), queryFn: () => listPublishedSoc2Policies(slug), enabled: !!slug, retry: false });
  const mine = useQuery({ queryKey: pendingPolicyKeys.mine(slug), queryFn: () => listMyPolicyAcknowledgments(slug), enabled: !!slug, retry: false });
  const acked = new Set((mine.data?.acknowledgments ?? []).map(a => a.version_id));
  const pending: PublishedPolicy[] = published.data && mine.data
    ? published.data.policies.filter(p => p.requires_acknowledgment === 1 && !acked.has(p.version_id))
    : [];
  return { pending, published, mine };
}

export function PendingPoliciesRow({ slug: slugProp }: { slug?: string }) {
  const ws = useOptionalWorkspace();
  const slug = slugProp ?? ws?.workspace.slug ?? '';
  const { pending } = usePendingPolicies(slug);
  if (!slug || pending.length === 0) return null;
  const n = pending.length;
  return <section className="v96-pending" aria-label="Policies to acknowledge">
    <StatusGlyph kind="alert" />
    <span className="v96-pending-text"><strong>{`Policies to acknowledge (${n})`}</strong>{n === 1 ? ` · ${pending[0]!.title}` : ''}</span>
    <Link className="v75-action v96-pending-action" to={`/w/${slug}/app/policies?acknowledge=1`}>Review and acknowledge</Link>
  </section>;
}
